import { mtFetch, type FetchResult } from "./mtFetch";
import type {
  LangCode,
  PretranslateRequest,
  PretranslationResponse,
  TranslateDebugResponse,
  TranslateEditorResponse,
  TranslateRequest,
} from "./types";

export function pretranslate(
  body: PretranslateRequest,
): Promise<FetchResult<PretranslationResponse>> {
  return mtFetch<PretranslationResponse>("/v1/pretranslate", {
    method: "POST",
    body,
  });
}

export function translate(
  body: TranslateRequest,
): Promise<FetchResult<TranslateEditorResponse>> {
  const { response_view = "editor", ...rest } = body;
  return mtFetch<TranslateEditorResponse>("/v1/translate", {
    method: "POST",
    body: { ...rest, response_view },
  });
}

/** Same endpoint, debug view. Carries full enforcement.events / quality
 * checks / pretranslate that editor view drops. Used when we want a
 * complete snapshot to store on the draft for the editor UI. */
export function translateDebug(
  body: Omit<TranslateRequest, "response_view">,
): Promise<FetchResult<TranslateDebugResponse>> {
  return mtFetch<TranslateDebugResponse>("/v1/translate", {
    method: "POST",
    body: { ...body, response_view: "debug" },
  });
}

// ---------------------------------------------------------------------------
// Быстрая интерактивная ручка «Перевести абстракт».
// Синхронный лёгкий путь (терминология + MT, без тяжёлой полной обработки).
// Сервер: 200 {status:"done", translated_text} если есть слот; 202
// {status:"queued", batch_id} при перегрузе (кладётся в priority-очередь) —
// тогда коротко поллим GET .../{batch_id} до готовности.
// ---------------------------------------------------------------------------

export interface AbstractTranslateRequest {
  source_lang: LangCode;
  target_lang: LangCode;
  text: string;
  backend?: string;
}

export interface AbstractTranslateResponse {
  schema_version: string;
  status: "done" | "queued" | "running" | "failed" | "partial";
  translated_text?: string | null;
  error?: string | null;
  batch_id?: string;
  backend?: string;
}

export function translateAbstract(
  body: AbstractTranslateRequest,
): Promise<FetchResult<AbstractTranslateResponse>> {
  return mtFetch<AbstractTranslateResponse>("/v1/translate/abstract", {
    method: "POST",
    body,
    silent: true, // ошибки показываем сами (в onError мутации)
  });
}

export function pollAbstract(
  batchId: string,
): Promise<FetchResult<AbstractTranslateResponse>> {
  return mtFetch<AbstractTranslateResponse>(
    `/v1/translate/abstract/${encodeURIComponent(batchId)}`,
    { method: "GET", silent: true },
  );
}

const _sleep = (ms: number): Promise<void> =>
  new Promise((r) => setTimeout(r, ms));

/** Полный поток кнопки: POST → при 202 поллинг очереди до готовности.
 * ``pollIntervalMs``/``sleep`` инъектируемы для тестов. Бросает Error с
 * человекочитаемым сообщением при отказе/таймауте. */
export async function translateAbstractFlow(
  body: AbstractTranslateRequest,
  opts: {
    pollIntervalMs?: number;
    maxPolls?: number;
    sleep?: (ms: number) => Promise<void>;
  } = {},
): Promise<string> {
  const { pollIntervalMs = 1500, maxPolls = 60, sleep = _sleep } = opts;

  const res = await translateAbstract(body);
  if (!res.ok) throw new Error(res.error);
  if (res.status !== 202 && res.data.status === "done") {
    return res.data.translated_text ?? "";
  }

  const batchId = res.data.batch_id;
  if (!batchId) throw new Error("сервер не вернул идентификатор задачи");

  for (let i = 0; i < maxPolls; i += 1) {
    await sleep(pollIntervalMs);
    const poll = await pollAbstract(batchId);
    if (!poll.ok) throw new Error(poll.error);
    const status = poll.data.status;
    if (status === "done") return poll.data.translated_text ?? "";
    if (status === "failed" || status === "partial") {
      throw new Error(poll.data.error ?? "перевод не выполнен");
    }
    // queued / running — продолжаем поллить
  }
  throw new Error("перевод занял слишком долго — попробуйте ещё раз");
}
